import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { format, isValid, parse } from 'date-fns';
import { X, Send, DollarSign, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { PeriodPicker } from '@/components/ui/period-picker';
import costsService from './costsService';
import type { CostItem, ManualCostPayload } from './costsService';

const PROVIDERS = ['AWS', 'BUNNY', 'IPINFO', 'OTHER'];

const DAY_FORMAT = 'yyyy-MM-dd';

/** 'yyyy-MM-dd' → meia-noite local. `new Date('yyyy-MM-dd')` leria como UTC. */
function parseDay(value: string): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DAY_FORMAT, new Date());
  return isValid(date) ? date : undefined;
}

interface CostsFormProps {
  isOpen: boolean;
  onClose: () => void;
  editItem?: CostItem | null;
  onSaved: () => void;
}

export function CostsForm({ isOpen, onClose, editItem, onSaved }: CostsFormProps) {
  useLanguage();
  const [provider, setProvider] = useState('OTHER');
  const [serviceName, setServiceName] = useState('');
  const [amount, setAmount] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [description, setDescription] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editItem;

  useEffect(() => {
    if (editItem) {
      setProvider(editItem.provider || 'OTHER');
      setServiceName(editItem.service_name || '');
      setAmount(editItem.amountOriginal?.toString() || '');
      setPeriodStart(editItem.period_start ? editItem.period_start.split('T')[0] : '');
      setPeriodEnd(editItem.period_start ? editItem.period_start.split('T')[0] : '');
      setDescription('');
      setRecurring(editItem.recurring || false);
    } else {
      setProvider('OTHER');
      setServiceName('');
      setAmount('');
      setPeriodStart('');
      setPeriodEnd('');
      setDescription('');
      setRecurring(false);
    }
  }, [editItem, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceName.trim()) {
      toast.error(translateInterfaceText("Preencha o nome do serviço."));
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error(translateInterfaceText("Informe um valor válido."));
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error(translateInterfaceText("Selecione o período."));
      return;
    }

    try {
      setSubmitting(true);
      const payload: ManualCostPayload = {
        provider,
        service_name: serviceName.trim(),
        amount: Number(amount),
        period_start: periodStart,
        period_end: periodEnd,
        description: description.trim() || undefined,
        recurring: recurring || undefined,
      };

      if (isEditing && editItem) {
        await costsService.update(editItem.id_cost, payload);
        toast.success(translateInterfaceText("Custo atualizado com sucesso!"));
      } else {
        await costsService.createManual(payload);
        toast.success(translateInterfaceText("Despesa cadastrada com sucesso!"));
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao salvar custo."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[500px]">
        <div className="p-5 border-b border-slate-50 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-blue-600" />
            <h3 className="text-sm font-bold text-slate-900">{isEditing ? translateInterfaceText("Editar Custo") : translateInterfaceText("Nova Despesa")}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Provedor")}</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-100/50 focus:bg-white transition-all"
            >
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Nome do Serviço *")}</label>
            <input
              type="text"
              required
              placeholder={translateInterfaceText("Ex: IPINFO API, Amazon EC2...")}
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Valor (R$) *")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
            />
          </div>

          {/* Obrigatório: sem o `required` nativo, quem barra o envio é a checagem do handleSubmit. */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Período *")}</label>
            <PeriodPicker
              value={{ from: parseDay(periodStart), to: parseDay(periodEnd) }}
              onChange={({ from, to }) => {
                setPeriodStart(format(from, DAY_FORMAT));
                setPeriodEnd(format(to, DAY_FORMAT));
              }}
              disableFuture={false}
              className="h-[34px] min-h-0 sm:w-full"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Descrição (opcional)")}</label>
            <input
              type="text"
              placeholder={translateInterfaceText("Ex: Plano Pro - 10k requests/mês")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <input
              type="checkbox"
              id="recurring"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <label htmlFor="recurring" className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
              <Repeat size={14} className="text-blue-500" />
              {translateInterfaceText("Despesa recorrente")}</label>
          </div>
          <p className="text-[10px] text-slate-400 -mt-2">{translateInterfaceText("Será criada automaticamente no mesmo dia a cada mês")}</p>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-colors">
              {translateInterfaceText("Cancelar")}</button>
            <button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99] flex items-center gap-2">
              {submitting ? translateInterfaceText("Salvando...") : translateInterfaceText("Cadastrar")} <Send size={12} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CostsForm;
